import firestore from '@react-native-firebase/firestore';
import { UserProfile, UserRole, DriverAllowlistEntry } from '../types/UserProfile';
import { ErrorLogger } from '../utils/errorLogger';
import { mapAllowlistEntryDoc } from '../utils/mappers';

const ALLOWLIST_COLLECTION = 'driverAllowlist';

/** Normalize an email for use as a stable allowlist document id. */
const normEmail = (email: string): string => email.trim().toLowerCase();

/**
 * Thrown by resolveProfile when a signed-in user is not on the allowlist. The
 * auth layer catches this to sign the user out.
 */
export class NotAuthorizedError extends Error {
  constructor() {
    super('This account is not authorized to access this app.');
    this.name = 'NotAuthorizedError';
  }
}

export class UserService {
  private static getAllowlistCollection() {
    return firestore().collection(ALLOWLIST_COLLECTION);
  }

  /**
   * Resolve the signed-in user's profile from the allowlist (the single source
   * of truth for who may access the app and their role). The allowlist is keyed
   * by normalized email and read with a direct document get (prod-safe).
   *
   * If the user's email is on the allowlist, an in-memory profile is returned —
   * nothing is persisted, so identity is never tied to a specific uid. If not,
   * NotAuthorizedError is thrown and the auth layer signs the user out.
   */
  static async resolveProfile(
    uid: string,
    email: string,
    displayName: string,
  ): Promise<UserProfile> {
    const key = normEmail(email);
    try {
      const entryDoc = await this.getAllowlistCollection().doc(key).get();
      // Use .data() presence, not .exists — @react-native-firebase exposes
      // exists() as a method while firebase/compat (web) exposes it as a boolean
      // property, so `.exists()` throws on web.
      const entry = entryDoc.data() as Partial<DriverAllowlistEntry> | undefined;

      if (!entry) {
        throw new NotAuthorizedError();
      }

      const role: UserRole = (entry.role as UserRole) ?? 'driver';
      return {
        uid,
        role,
        email,
        displayName,
        createdAt: new Date(),
        ...(entry.assignedTruckId !== undefined
          ? { assignedTruckId: entry.assignedTruckId }
          : {}),
      };
    } catch (error) {
      // Deny is an expected outcome — surface it for sign-out, don't mask it.
      if (error instanceof NotAuthorizedError) {
        throw error;
      }
      ErrorLogger.logError(error, 'UserService.resolveProfile');
      throw new Error('Failed to resolve user profile');
    }
  }

  /**
   * Add an entry to the allowlist. Role determines access; truck only required
   * for drivers. Docs are keyed by normalized email so a user can read their own
   * entry with a direct get, and re-adding the same email is idempotent.
   */
  static async addDriverToAllowlist(
    entry: Omit<DriverAllowlistEntry, 'id' | 'createdAt' | 'addedBy'>,
    addedBy: string,
  ): Promise<void> {
    try {
      const email = normEmail(entry.email);
      await this.getAllowlistCollection().doc(email).set({
        ...entry,
        email,
        addedBy,
        createdAt: new Date(),
      });
    } catch (error) {
      ErrorLogger.logError(error, 'UserService.addDriverToAllowlist');
      throw new Error('Failed to add driver to allowlist');
    }
  }

  /**
   * Update role or truck assignment on an existing allowlist entry.
   */
  static async updateAllowlistEntry(
    entryId: string,
    updates: { role: UserRole; assignedTruckId?: string | null },
  ): Promise<void> {
    try {
      await this.getAllowlistCollection().doc(entryId).update(updates);
    } catch (error) {
      ErrorLogger.logError(error, 'UserService.updateAllowlistEntry');
      throw new Error('Failed to update allowlist entry');
    }
  }

  /**
   * Remove a driver from the allowlist by entry id.
   */
  static async removeDriverFromAllowlist(entryId: string): Promise<void> {
    try {
      await this.getAllowlistCollection().doc(entryId).delete();
    } catch (error) {
      ErrorLogger.logError(error, 'UserService.removeDriverFromAllowlist');
      throw new Error('Failed to remove driver from allowlist');
    }
  }

  /**
   * Get all entries in the driverAllowlist.
   */
  static async getAllowlistEntries(): Promise<DriverAllowlistEntry[]> {
    try {
      const snapshot = await this.getAllowlistCollection().get();
      return snapshot.docs.map(doc => mapAllowlistEntryDoc(doc));
    } catch (error) {
      ErrorLogger.logError(error, 'UserService.getAllowlistEntries');
      return [];
    }
  }
}

export default UserService;
